import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Database, RefreshCw, MapPin, Leaf, Mountain, TreePine, Layers, Pickaxe,
  AlertTriangle, Trees, Landmark, Activity, CheckCircle2, XCircle, AlertCircle,
  ExternalLink, Calendar, Clock,
} from "lucide-react";
import { authFetch } from "@/hooks/use-auth";

interface ModuleHealth {
  module: string;
  label: string;
  table: string;
  page: string;
  count: number;
  ultima_importacao: string | null;
  status: "ok" | "parcial" | "vazio";
}

interface HealthResponse {
  timestamp: string;
  modules: ModuleHealth[];
  total_ok: number;
  total_parcial: number;
  total_vazio: number;
}

interface ModuleSchedule {
  id: number;
  modulo: string;
  label: string;
  cron_expression: string;
  ativo: boolean;
  proxima_execucao: string | null;
}

const MODULE_ICONS: Record<string, typeof Database> = {
  cadastro: Database,
  agrofit: Leaf,
  sicar: MapPin,
  anm: Pickaxe,
  cprm: Mountain,
  sgb: Layers,
  funai: TreePine,
  ibama: AlertTriangle,
  icmbio: Trees,
  pgfn: Landmark,
};

const VERTICALS: { label: string; icon: typeof Database; color: string; modules: string[] }[] = [
  { label: "Mineracao", icon: Pickaxe, color: "text-amber-500", modules: ["anm", "cprm", "sgb"] },
  { label: "Agro & Fundiario", icon: Leaf, color: "text-green-500", modules: ["sicar", "agrofit", "cadastro"] },
  { label: "Ambiental", icon: Trees, color: "text-emerald-500", modules: ["ibama", "icmbio", "funai"] },
  { label: "Fiscal", icon: Landmark, color: "text-blue-500", modules: ["pgfn"] },
];

function fmtNum(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function relativeTime(d: string | null) {
  if (!d) return "—";
  const diff = new Date(d).getTime() - Date.now();
  if (diff < 0) return "Agora";
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  const mins = Math.floor(diff / 60000);
  return `${mins}min`;
}

function KpiRow({ modules }: { modules: ModuleHealth[] }) {
  const totalRecords = modules.reduce((s, m) => s + m.count, 0);
  const okCount = modules.filter((m) => m.status === "ok").length;
  const emptyCount = modules.filter((m) => m.status === "vazio").length;

  return (
    <div className="grid grid-cols-3 gap-4">
      <Card className="bg-muted/30">
        <CardContent className="pt-4 text-center">
          <p className="text-xs text-muted-foreground">Registros totais</p>
          <p className="text-2xl font-bold tabular-nums" data-testid="kpi-total-records">{fmtNum(totalRecords)}</p>
        </CardContent>
      </Card>
      <Card className="bg-muted/30">
        <CardContent className="pt-4 text-center">
          <p className="text-xs text-muted-foreground">Modulos ativos</p>
          <p className="text-2xl font-bold text-green-400 tabular-nums" data-testid="kpi-modules-ok">{okCount} / {modules.length}</p>
        </CardContent>
      </Card>
      <Card className="bg-muted/30">
        <CardContent className="pt-4 text-center">
          <p className="text-xs text-muted-foreground">Modulos vazios</p>
          <p className={`text-2xl font-bold tabular-nums ${emptyCount > 0 ? "text-red-400" : "text-green-400"}`} data-testid="kpi-modules-empty">{emptyCount}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function VerticalSection({ vertical, modules }: { vertical: typeof VERTICALS[0]; modules: ModuleHealth[] }) {
  const Icon = vertical.icon;
  const relevantModules = modules.filter((m) => vertical.modules.includes(m.module));
  if (relevantModules.length === 0) return null;

  return (
    <div className="space-y-2" data-testid={`vertical-${vertical.label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 px-1">
        <Icon className={`h-4 w-4 ${vertical.color}`} />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{vertical.label}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {relevantModules.map((m) => {
          const MIcon = MODULE_ICONS[m.module] || Database;
          const isOk = m.status === "ok";
          const isParcial = m.status === "parcial";
          const borderBg = isOk
            ? "border-green-800/50 bg-green-950/20 hover:bg-green-950/40"
            : isParcial
            ? "border-yellow-800/50 bg-yellow-950/20 hover:bg-yellow-950/40"
            : "border-red-800/50 bg-red-950/20 hover:bg-red-950/40";
          const iconColor = isOk ? "text-green-500" : isParcial ? "text-yellow-500" : "text-red-500";
          const countColor = isOk ? "text-green-400" : isParcial ? "text-yellow-400" : "text-red-400";
          const badgeLabel = isOk ? "OK" : isParcial ? "Parcial" : "Vazio";
          const badgeClass = isOk
            ? "border-green-700 text-green-400"
            : isParcial
            ? "border-yellow-700 text-yellow-400"
            : "border-red-700 text-red-400";

          return (
            <Link key={m.module} href={m.page}>
              <div className={`rounded-lg border p-3 cursor-pointer transition-all hover:shadow-md ${borderBg}`} data-testid={`health-module-${m.module}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <MIcon className={`h-4 w-4 ${iconColor}`} />
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${badgeClass}`}>{badgeLabel}</Badge>
                </div>
                <div className="text-xs font-medium truncate">{m.label}</div>
                <div className={`text-lg font-bold ${countColor}`}>{fmtNum(m.count)}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                  {m.ultima_importacao ? fmtDate(m.ultima_importacao) : "Sem importacao"}
                </div>
                {m.status === "vazio" && (
                  <div className="flex items-center gap-1 mt-1.5 text-[10px] text-primary">
                    <ExternalLink className="h-3 w-3" /> Importar
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function NextSchedulesCard() {
  const schedulesQ = useQuery<ModuleSchedule[]>({
    queryKey: ["/api/scheduler/modules"],
    queryFn: () => authFetch("/api/scheduler/modules"),
    refetchInterval: 60000,
  });

  const schedules = schedulesQ.data || [];
  const upcoming = schedules
    .filter((s) => s.ativo && s.proxima_execucao)
    .sort((a, b) => new Date(a.proxima_execucao!).getTime() - new Date(b.proxima_execucao!).getTime())
    .slice(0, 5);

  if (schedulesQ.isLoading) {
    return <Skeleton className="h-32 w-full rounded-lg" />;
  }

  if (upcoming.length === 0) return null;

  return (
    <Card data-testid="card-next-schedules">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4 text-indigo-500" />
          Proximas execucoes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {upcoming.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
              <span className="text-muted-foreground">{s.label}</span>
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs tabular-nums font-medium">
                  {relativeTime(s.proxima_execucao)}
                </span>
              </div>
            </div>
          ))}
        </div>
        <Link href="/rotinas">
          <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" data-testid="button-view-rotinas">
            Ver todas as rotinas
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function HomePage() {
  const healthQ = useQuery<HealthResponse>({
    queryKey: ["/api/health/modules"],
    queryFn: () => authFetch("/api/health/modules"),
    refetchInterval: 30000,
  });

  const data = healthQ.data;
  const modules = data?.modules || [];

  return (
    <div className="space-y-6" data-testid="page-home">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-welcome">MAVRION HUB API</h1>
          <p className="text-sm text-muted-foreground">Plataforma centralizada de dados — Mineracao, Agro, Ambiental, Fiscal</p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> {data.total_ok}
              </span>
              {(data.total_parcial ?? 0) > 0 && (
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5 text-yellow-500" /> {data.total_parcial}
                </span>
              )}
              <span className="flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5 text-red-500" /> {data.total_vazio}
              </span>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => healthQ.refetch()} data-testid="button-refresh-health">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${healthQ.isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {healthQ.isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Array(9).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
          </div>
        </div>
      ) : (
        <>
          <KpiRow modules={modules} />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-5">
              {VERTICALS.map((v) => (
                <VerticalSection key={v.label} vertical={v} modules={modules} />
              ))}
            </div>
            <div className="space-y-4">
              <NextSchedulesCard />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
